import { Component } from '@angular/core';
import { OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { AlertsService } from '../../services/alerts.service';
import { OrdersService } from '../../services/orders.service';
import { ConfirmComponent } from '../../components/confirm/confirm.component';
import { UserService } from '../../services/user.service';
import dayjs from 'dayjs';
import flatpickr from 'flatpickr';


/**
 * Main downloads management component.
 */
@Component({
  selector: 'downloads',
  templateUrl: './downloads.component.html',
  styleUrl: './downloads.component.css'
})
export class DownloadsComponent implements OnInit, OnDestroy {
	
	private userServiceSubscription!: Subscription;
	
	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};
	
	datePicker: any;
	
	orderOn: boolean = false;
	orders: any[] = [];
	currentOrder: any;
	selectedOrders = {
		all: false,
		selected: [] as number[]
	};	
	filters = {
		status: '',
		dateRange: '',
		startDate: '',
		endDate: '',
		uuid: ''
	};
	currentConstraints = {
		user: '',
		orderUUID: '',
		beginCreatedDate: '',
		endCreatedDate: '',
		orderStatuses: [] as string[]
	};
	pagination = {
		page: 1,
		elementsPerPage: 10,
		elementsPerPageSizes: [10, 20, 50],
		sorting: {
			sort: 'created,desc',
			description: 'Newest'
		},
		totalElements: 0,
		pageChanged: (newPage: any) => {
			this.pagination.page = newPage;
		    this.loadDownloads(this.pagination.page);
		}		
	};	
	fetching: boolean = false;
	
	constructor(
		private modalService: NgbModal,
		private userService: UserService,
		private alertsService: AlertsService,
		private ordersService: OrdersService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.pagination.sorting.description = this.LOCALIZATION.newest;
		this.userPermissions = this.userService.getUserPermissionsMap();
		this.currentConstraints.user = this.userService.user.username ?? '';
	}
	
	ngOnInit(): void {
		this.userServiceSubscription = this.userService.broadcastEvent.subscribe((eventId: string) => {
			if ("localizationLoaded" === eventId) {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			} else if ("userPrefsUpdate" === eventId) {
				this.userPermissions = this.userService.getUserPermissionsMap();
			}
		});		
		
		this.initFields();	
	}
	
	ngOnDestroy(): void {
		if (this.userServiceSubscription) {
			this.userServiceSubscription.unsubscribe();
		}
	}
	
	private initFields(): void {
		if (this.userPermissions['ROLE_ORDER_MANAGEMENT'] || this.userPermissions['ROLE_ORDER_VIEW'] || this.userPermissions['ROLE_PRODUCT_DOWNLOAD']) {
		    this.orderOn = true;
		    this.loadDownloads();
		} else {
		    this.orderOn = false;
		}
	}
	
	/**
	 * Loads the download orders using the current constraints.
	 */
	loadDownloads(page?: number): void {
		this.fetching = true;

		this.selectedOrders = {
			all: false,
			selected: []
		};
		
		if (page) {
			this.pagination.page = page;
		}
		
		let index = this.pagination.page - 1;
		
		this.ordersService.getOrders(index, this.pagination, this.currentConstraints).then(
			(data: any) => {
				if (!data) {
					this.orders = [];
				} else {
					this.orders = data.content;
					this.pagination.page = data.page.number + 1;
					this.pagination.elementsPerPage = data.page.size;
					this.pagination.totalElements = data.page.totalElements;
				}
				this.fetching = false;
			}, 
			(err: any) => {
				console.error("Failed to fetch download orders:", err);
				this.orders = [];
				this.fetching = false;
			}
		);
	}
	
	downloadOrder(): void {
		this.alertsService.info({
		    title: this.LOCALIZATION.downloadStarted,
		    message: this.LOCALIZATION.processingWaitMessage
		});		
	}	
	
	/**
	 * Deletes the download order with the given id.
	 */
	deleteOrder(id: number): void {
		this.ordersService.deleteOrder(id).then(
			() => {
			    this.initFields();
			}, 
			() => {
			    this.alertsService.error({
			        message: "Failed to delete the download order.",
			        delay: 5000
			    });
			    console.error("Failed to delete order ID %s", id);
			}
		);		
	}	
	
	onDeleteOrders() {
		const modalRef = this.modalService.open(ConfirmComponent, {
			size: 'sm'
		});

		modalRef.componentInstance.modal = {
			callerName: 'deleteOrders',
			title: this.LOCALIZATION.deleteOrdersHeader,
			message: this.LOCALIZATION.deleteOrdersMessage,
			type: 'danger'
		};

		modalRef.result.then(
			(result) => {
				if (result.result?.answer === 'ok') {
					this.ordersService.deleteOrders(this.selectedOrders.selected).then(
						(response: any) => {
							this.loadDownloads();
						}, 
						(error: any) => {
							let errorMessage = error?.data || this.LOCALIZATION.deleteOrdersError;
						    this.alertsService.error({
						        message: errorMessage
						    });
						}
					);	
				}
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}	
	
	clear(): void {
		if (this.datePicker) {
			this.datePicker.clear();
		}
		
		this.filters.uuid = '';
		this.filters.dateRange = '';
		this.filters.startDate = '';
		this.filters.endDate = '';
		this.filters.status = '';
		
		this.filter();		
	}
	
	filter(): void {
		if (this.datePicker) {
			let [start, end] = this.datePicker.selectedDates;
			
			if (start) {
				if (!end) end = new Date(start.getTime());
				start.setHours(0, 0, 0, 0);
				end.setHours(23, 59, 59, 999);				
				console.log("Selected range:", {
				  start: dayjs(start).format("MM/DD/YYYY h:m:s"),
				  end: dayjs(end).format("MM/DD/YYYY h:m:s"),
				});				
				this.filters.startDate = start.getTime();
				this.filters.endDate = end.getTime();
			}
		}

		this.currentConstraints['orderUUID'] = '';
		this.currentConstraints['beginCreatedDate'] = '';
		this.currentConstraints['endCreatedDate'] = '';
		this.currentConstraints['orderStatuses'] = [];

		if (this.filters.uuid) {
		    this.currentConstraints['orderUUID'] = this.filters.uuid;
		}
		if (this.filters.startDate) {
		    this.currentConstraints['beginCreatedDate'] = this.filters.startDate;
		}
		if (this.filters.endDate) {
		    this.currentConstraints['endCreatedDate'] = this.filters.endDate;
		}
		if (this.filters.status) {
		    this.currentConstraints['orderStatuses'] = [this.filters.status.toUpperCase()];
		}
		this.loadDownloads(1);
	}
	
	pageSizeChanged(size: number): void {
		this.pagination.elementsPerPage = size;
		this.loadDownloads(1);
	}

	sortingChanged(sortObj: any = {}): void {
		this.pagination.sorting = sortObj;
		this.loadDownloads(1);
	}
	
	toggleOrder(id: number): void {
		const index = this.selectedOrders.selected.indexOf(id);
		if (index > -1) {
		    this.selectedOrders.selected.splice(index, 1);
		} else {
		    this.selectedOrders.selected.push(id);
		}
		this.selectedOrders.all = this.orders.length === this.selectedOrders.selected.length;		
	}	

	toggleAllOrders(): void {
		let toggleStatus = !this.selectedOrders.all;
		this.selectedOrders.selected = [];
		this.orders.forEach((order) => {
		  order.selected = toggleStatus;
		  if (toggleStatus) {
		    this.selectedOrders.selected.push(order.id);
		  }
		});
	}	
	
	openDatePicker(id: string): void {
		if (!this.datePicker) {
			const opts = this.userService.getDatePickerOptions();
			opts.mode = "range";
			delete opts.defaultDate;

			const element = document.getElementById(id);
			if (element) {
				this.datePicker = flatpickr(element, opts);
			}
		}

		if (this.datePicker && this.datePicker.isOpen) {
			this.datePicker.close();
		} else if (this.datePicker) {
			this.datePicker.open();
		}
  }
	
  closeDropdown(dropdown: NgbDropdown) {
	if(dropdown && dropdown.isOpen()) {
    	dropdown.close(); // closes the dropdown
    }
  }
}
